import React, { useState, useEffect } from "react";
import {useLocation, useNavigate } from "react-router-dom";
import "./Details.css";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import { FaExternalLinkAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useTranslation } from "react-i18next";

function Details() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("backend");
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const location = useLocation();
  const { subscription_id, name, email } = location.state;
  const [product, setProduct] = useState({});
  const [originalProduct, setOriginalProduct] = useState({});
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const token = Cookies.get("token");
  const [plans, setPlans] = useState([]);
  const [selectedPlanName, setSelectedPlanName] = useState("");
  const [plan, setPlan] = useState(null);
  let isAdmin = false;
  if (token) {
    const decodedToken = jwtDecode(token);
    isAdmin = decodedToken.isAdmin;
  }

  useEffect(() => {
    if (!isAdmin) {
      setIsAdminUser(true);
    }

    if (subscription_id) {
      const fetchDetails = async () => {
        try {
          const productResponse = await fetch(
            `${BASE_URL}/detailproduct/${subscription_id}`
          );
          const productData = await productResponse.json();
          if (productResponse.ok) {
            setOriginalProduct(productData.products);
            setProduct(productData.products);
            setPlan(
              i18n.language === "ar"
                ? productData.products?.plan_name_ar || ""
                : productData.products?.plan_name || ""
            );
            setPlans(productData.plans);
          } else {
            alert(productData.message);
          }
        } catch (err) {
          console.error("Error fetching details:", err.message);
          alert("Error fetching details: " + err.message);
        }
      };

      fetchDetails();
    } else {
      console.error("User email is undefined or not provided.");
    }
  }, [subscription_id, BASE_URL]);

  const handleChange = (e, type) => {
    const { name, value } = e.target;
    if (type === "product") {
      setProduct((prevProduct) => {
        const updatedProduct = { ...prevProduct, [name]: value };
        return updatedProduct;
      });
    }
  };

  const hasChanges = () => {
    return (
      product.plan_name !== originalProduct.plan_name ||
      product.expiry_date !== originalProduct.expiry_date ||
      product.start_date !== originalProduct.start_date ||
      product.status !== originalProduct.status ||
      product.url !== originalProduct.url ||
      product.id !== originalProduct.id ||
      product.password !== originalProduct.password ||
      product.domain_name !== originalProduct.domain_name
    );
  };
  const [loading, setLoading] = useState(false);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!hasChanges()) {
      toast.error("No changes made to the details.");
      return;
    }
    if (product.status === "deleted") {
      const promptDelete = window.prompt(t("confirmDelete"));

      if (promptDelete !== "CONFIRM") {
        alert(t("deleteCanceled"));
        return;
      }
    }

    try {
      setLoading(true); // start loading
      const response = await fetch(
        `${BASE_URL}/updateproduct/${subscription_id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...product,
            password: product.password ? product.password : undefined,
            name,
            email,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        alert(result.message);
        setOriginalProduct(product);
        navigate("/dashboard/subscriptions");
      } else {
        alert(result.error);
      }
    } catch (err) {
      console.log(err);
    }
    finally {
      setLoading(false);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();
    navigate(-1);
  };
  const handleUpgradeClick = () => {
    if (!selectedPlan) {
      toast.error(t("selectPlan"));
      return;
    }
    // Navigate to the payments page with state
    navigate("/dashboard/payments", {
      state: { selectedPlan, subscription_id },
    });
  };

  
  return (
    <div className="edit-contain">
      {loading && (
  <div
    style={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      backgroundColor: "rgba(38, 48, 67, 0.5)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    }}
  >
    <div
      style={{
        border: "6px solid #e0e0e0",
        borderTop: "6px solid #0f4b60",
        borderRadius: "50%",
        width: "60px",
        height: "60px",
        animation: "spin 1s linear infinite",
        marginBottom: "20px",
      }}
    />
    <div style={{ color: "#fff", fontSize: "20px", fontWeight: "600" }}>
      Please wait...
    </div>

    <style>
      {`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}
    </style>
  </div>
)}


      <h2>
        {t("details")}{" "}
        <p style={{ fontSize: "20px" }}>
          {t("subs_id")} {product.subscription_id}
        </p>
      </h2>
      <div className="container-edit details-container">
        {isAdmin ? (
          <form onSubmit={handleUpdate} className="edit-form">
            {/* Package Name */}
            <div className="form-group">
              <label>{t("pkg_name")}</label>
              <div className="input-container">
                <select
                  name="plan_name"
                  value={product.plan_name}
                  onChange={(e) => handleChange(e, "product")}
                  required
                >
                  <option value={product.plan_name}>{product.plan_name}</option>
                  {plans.length > 0 &&
                    plans.map((plan, index) => {
                      if (plan.plan_name !== product.plan_name) {
                        return (
                          <option key={index} value={plan.plan_name}>
                            {plan.plan_name}
                          </option>
                        );
                      }
                      return null;
                    })}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>{t("creation_date")}</label>
              <div className="input-container">
                <input
                  type="date"
                  name="start_date"
                  value={product.start_date || ""}
                  onChange={(e) => handleChange(e, "product")}
                  required
                />
              </div>
            </div>

            {/* Expiry Date */}
            <div className="form-group">
              <label>{t("expiry")}:</label>
              <div className="input-container">
                <input
                  type="date"
                  name="expiry_date"
                  value={product.expiry_date || ""}
                  onChange={(e) => handleChange(e, "product")}
                  required
                />
              </div>
            </div>

            {/* User Fields */}
            <div className="form-group">
              <label>{t("customer_name")}</label>
              <div className="input-container">
                <p>{name}</p>
              </div>
            </div>

            <div className="form-group">
              <label>{t("domain")}:</label>
              <div className="input-container">
                <input
                  type="text"
                  name="domain_name"
                  value={product.domain_name || ""}
                  onChange={(e) => handleChange(e, "product")}
                />
              </div>
            </div>

            {/* Credentials Section */}
            <div className="credentials-container">
              <h3>{t("credetials")}</h3>

              <div className="form-group">
                <label>{t("url")}</label>
                <div className="input-container">
                  <input
                    type="text"
                    name="url"
                    value={product.url || ""}
                    onChange={(e) => handleChange(e, "product")}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t("id")}</label>
                <div className="input-container">
                  <input
                    type="text"
                    name="id"
                    value={product.id || ""}
                    onChange={(e) => handleChange(e, "product")}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t("password")}</label>
                <div className="input-container">
                  <input
                    type="text"
                    name="password"
                    value={product.password || ""}
                    onChange={(e) => handleChange(e, "product")}
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="form-group">
              <label>{t("status")}:</label>
              <div className="input-container">
                <select
                  name="status"
                  value={product.status || "active"}
                  onChange={(e) => handleChange(e, "product")}
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="deleted">Deleted</option>
                  <option value="progress">Progress</option>
                  <option value="create">Create</option>
                </select>
              </div>
            </div>
            <div className="save-back-btn-class">
              <button className="back-btn" onClick={handleBack} disabled={loading}>
                {t("back")}
              </button>
              <button type="submit" className="submit-button" style={{
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.6 : 1
              }}
                disabled={loading}>
                {loading ? t("Saving") + "..." : t("save")}
              </button>
            </div>
          </form>
        ) : (
          <div className="view-only-details">
            {/* Package Name */}
            <div className="form-group">
              <label>{t("pkg_name")}</label>
              <p>{plan}</p>
            </div>

            {/* Creation Date */}
            <div className="form-group">
              <label>{t("creation_date")}</label>
              <p>{product.start_date}</p>
            </div>

            {/* Expiry Date */}
            <div className="form-group">
              <label>{t("expiry")}:</label>
              <p>{product.expiry_date}</p>
            </div>

            {/* Client Name */}
            {isAdmin ? (
              <div className="form-group">
                <label>{t("client_name")}</label>
                <p>{name}</p>
              </div>
            ) : null}

            {/* Credentials Section */}
            <div className="credentials-container">
              <h3>{t("credetials")}</h3>
              <div className="form-group">
                <label>{t("url")}</label>
                <p>
                  <a
                    href={`https://${product.url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#0f4b60" }}
                  >
                    {product.url}
                    <FaExternalLinkAlt style={{ marginLeft: "20px" }} />
                  </a>
                </p>
              </div>

              <div className="form-group">
                <label>{t("id")}</label>
                <p>{product.id}</p>
              </div>

              <div className="form-group">
                <label>{t("password")}</label>
                <p>{product.password}</p>
              </div>
            </div>

            {/* Status */}
            <div className="form-group">
              <label>{t("status")}:</label>
              <p>
                {" "}
                <span
                  style={{
                    display: "inline-block",
                    borderRadius: "4px",
                    padding: "2px 8px",
                    fontWeight: "bold",
                    color:
                      product.status === "active"
                        ? "#4caf50"
                        : product.status === "suspended"
                          ? "#9e9e9e"
                          : product.status === "progress"
                            ? "#2196F3"
                            : product.status === "create"
                              ? "Orange"
                              : "#8b4513",

                    border: `2px solid ${product.status === "active"
                        ? "#4caf50"
                        : product.status === "suspended"
                          ? "#9e9e9e"
                          : product.status === "progress"
                            ? "#2196F3"
                            : product.status === "create"
                              ? "Orange"
                              : "#8b4513"
                      }`,
                  }}
                >
                  {product.status === "active"
                    ? "ACTIVE"
                    : product.status === "suspended"
                      ? "SUSPENDED"
                      : product.status === "progress"
                        ? "PROGRESS"
                        : product.status === "create"
                          ? "CREATED"
                          : "DELETED"}
                </span>
              </p>
            </div>
            <div className="form-group">
              <label>{t("pkg_name")}</label>
              <div className="input-container" style={{ textAlign: "end" }}>
                <select
                  name="plan_name"
                  value={product.plan_name} // Use selectedPlanName for value
                  onChange={(e) => {
                    const selectedPlanName = e.target.value;

                    // Update the selected plan name
                    setSelectedPlanName(selectedPlanName);

                    // Find and update the selected plan details
                    const selectedPlan = plans.find(
                      (plan) => plan.plan_name === selectedPlanName
                    );

                    if (selectedPlan) {
                      setSelectedPlan(selectedPlan); // Store full selected plan details
                      setProduct((prevProduct) => ({
                        ...prevProduct,
                        plan_name: selectedPlan.plan_name, // Update product if needed
                      }));
                    }
                  }}
                  required
                  style={{ width: "60%" }}
                >
                  <option value="">{t("select_plan")}</option>
                  {plans.length > 0 &&
                    plans.map((allplan, index) => {
                      // Exclude the current plan from the dropdown
                      if (
                        allplan.plan_name !== plan &&
                        allplan.plan_name_ar !== plan
                      ) {
                        return (
                          <option key={index} value={allplan.plan_name}>
                            {`${i18n.language === "ar"
                                ? allplan.plan_name_ar
                                : allplan.plan_name
                              } - SAR ${allplan.amount}`}
                          </option>
                        );
                      }
                      return null;
                    })}
                </select>
              </div>
            </div>

            <div className="save-back-btn-class" style={{ marginTop: "0" }}>
              <button className="back-btn" onClick={handleBack}>
                {t("back")}
              </button>
              <button
                className="submit-button"
                style={{ backgroundColor: "#4CAF50" }}
                onClick={handleUpgradeClick}
              >
                {t("upgrade")}
              </button>
            </div>
          </div>
        )}
      </div>
      <ToastContainer position="bottom-center" />
    </div>
  );
}

export default Details;
